import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  alpha,
  useTheme,
  Fade,
  Grow,
  CircularProgress,
  Tooltip,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  Label as LabelIcon, 
  Category as CategoryIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import axiosInstance from '../../axiosInstance';
import { Category } from '../types/forum.types';

interface CategoryManagementProps {
  categories: Category[];
  onCategoryChange: () => void;
}

export const CategoryManagement: React.FC<CategoryManagementProps> = ({
  categories,
  onCategoryChange,
}) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<number | null>(null);

  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams({
        name: newCategoryName.trim()
      });

      await axiosInstance.post(`/forum/categories/?${params.toString()}`);
      setNewCategoryName('');
      setIsAddingCategory(false);
      onCategoryChange();
    } catch (error) {
      console.error('Error adding category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    setDeletingCategory(categoryId);
    try {
      await axiosInstance.delete(`/forum/categories/${categoryId}`);
      onCategoryChange();
    } catch (error) {
      console.error('Error deleting category:', error);
    } finally {
      setDeletingCategory(null);
    }
  };

  return (
    <Box>
      <Paper 
        elevation={isDarkTheme ? 3 : 1}
        sx={{ 
          p: 3, 
          mb: 3,
          bgcolor: isDarkTheme ? alpha(theme.palette.background.paper, 0.4) : theme.palette.background.paper,
          borderRadius: isDarkTheme ? '8px' : '0px',
          border: '1px solid',
          borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : theme.palette.divider
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
            <CategoryIcon sx={{ 
              mr: 1.5, 
              color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
              fontSize: 28
            }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Forum Categories
            </Typography>
            <Chip 
              label={categories.length} 
              size="small" 
              sx={{ 
                ml: 1.5,
                bgcolor: isDarkTheme ? alpha('#64AFE3', 0.15) : alpha(theme.palette.primary.main, 0.1),
                color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                fontWeight: 'bold',
                borderRadius: '4px'
              }} 
            />
          </Box>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setIsAddingCategory(true)}
            sx={{
              borderRadius: isDarkTheme ? '4px' : '0px',
              textTransform: 'none',
              bgcolor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
              color: isDarkTheme ? '#000000' : 'white',
              fontWeight: 'medium',
              px: 2.5,
              '&:hover': {
                bgcolor: isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: isDarkTheme ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Add Category
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your forum categories here. Categories help organize discussions by topic.
        </Typography>

        <Divider sx={{ mb: 2, borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : theme.palette.divider }} />

        {categories.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4, 
            bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.1),
            borderRadius: isDarkTheme ? '8px' : '0px',
            border: '1px dashed',
            borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : theme.palette.divider
          }}>
            <CategoryIcon 
              sx={{ 
                fontSize: 40, 
                color: alpha(theme.palette.text.secondary, 0.3),
                mb: 1
              }} 
            />
            <Typography color="text.secondary">
              No categories yet. Add your first category.
            </Typography>
          </Box>
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              overflow: 'hidden',
              borderRadius: isDarkTheme ? '8px' : '0px',
              borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
              bgcolor: 'transparent'
            }}
          >
            <List sx={{ p: 0 }}>
              {categories.map((category, index) => (
                <React.Fragment key={category.id}>
                  <ListItem
                    sx={{ 
                      py: 1.5,
                      bgcolor: isDarkTheme 
                        ? index % 2 === 0 ? alpha(theme.palette.background.secondary, 0.3) : 'transparent'
                        : index % 2 === 0 ? alpha(theme.palette.background.secondary, 0.1) : 'transparent',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        bgcolor: isDarkTheme 
                          ? alpha(theme.palette.background.secondary, 0.5) 
                          : alpha(theme.palette.background.secondary, 0.2),
                      }
                    }}
                    secondaryAction={
                      <Tooltip title="Delete category" arrow placement="left">
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteCategory(category.id)}
                          disabled={deletingCategory === category.id}
                          sx={{ 
                            color: isDarkTheme ? alpha(theme.palette.error.main, 0.8) : theme.palette.error.main,
                            '&:hover': {
                              bgcolor: isDarkTheme ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.error.main, 0.05),
                              color: theme.palette.error.main
                            },
                            borderRadius: isDarkTheme ? '4px' : '0px'
                          }}
                        >
                          {deletingCategory === category.id ? 
                            <CircularProgress size={20} color="inherit" /> : 
                            <DeleteIcon />
                          }
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar 
                        sx={{ 
                          bgcolor: isDarkTheme ? alpha('#64AFE3', 0.15) : alpha(theme.palette.primary.main, 0.1),
                          color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                          width: 36,
                          height: 36,
                          borderRadius: isDarkTheme ? '6px' : '0px'
                        }}
                      >
                        <LabelIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {category.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Created: {new Date(category.created_at).toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < categories.length - 1 && (
                    <Divider sx={{ borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : theme.palette.divider }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </Paper>

      <Dialog 
        open={isAddingCategory} 
        onClose={() => !isSubmitting && setIsAddingCategory(false)}
        PaperProps={{
          sx: { 
            borderRadius: isDarkTheme ? '8px' : '0px',
            bgcolor: isDarkTheme ? theme.palette.background.paper : theme.palette.background.paper,
            backgroundImage: 'none'
          }
        }}
        TransitionComponent={Grow}
        transitionDuration={300}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1, color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main }} />
            Add New Category
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: isDarkTheme ? '4px' : '0px',
                '& fieldset': {
                  borderColor: isDarkTheme ? alpha('#64AFE3', 0.3) : alpha(theme.palette.primary.main, 0.3)
                },
                '&:hover fieldset': {
                  borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main
                }
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
            Categories help users navigate and find relevant discussions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          pb: 2,
          borderTop: '1px solid',
          borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
        }}>
          <Button 
            onClick={() => setIsAddingCategory(false)} 
            disabled={isSubmitting}
            sx={{ 
              color: isDarkTheme ? alpha(theme.palette.text.primary, 0.7) : 'text.secondary',
              borderRadius: isDarkTheme ? '4px' : '0px',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained"
            disabled={isSubmitting || !newCategoryName.trim()}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            sx={{
              borderRadius: isDarkTheme ? '4px' : '0px',
              bgcolor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
              color: isDarkTheme ? '#000000' : 'white',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                bgcolor: isDarkTheme ? '#8ABFEB' : theme.palette.primary.dark
              }
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 